import React from 'react';
import {
  Box, Typography, TextField, Grid, Button, InputAdornment,
  Select, MenuItem, CircularProgress
} from '@mui/material';
import {
  Phone, CheckCircle, ErrorOutline, Language
} from '@mui/icons-material';

const ContactInformationStep = ({
  formik,
  inputValue,
  handlePhoneValueChange,
  inputRef,
  country,
  setCountry,
  defaultCountries,
  parseCountry,
  FlagImage,
  loading,
  isVerified,
  handleSendOTP,
  isOTPRequested,
  otp,
  setOTP,
  otpError,
  handleVerifyOTP
}) => {
  const textFieldStyles = {
    backgroundColor: '#d4d4d4',
    marginTop: '1em',
    borderRadius: '5px',
    borderBottom: '1.5px solid #bdd1e0',
    height: { xs: '2.5em', sm: '2.7em' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { height: { xs: '2.8em', sm: '3em' } },
      '&:hover fieldset': { borderColor: '#6794b6' },
      '&.Mui-focused fieldset': { borderColor: '#3b76a2', height: { xs: '2.8em', sm: '3em' } }
    },
    '& .MuiInputLabel-root': { color: '#13334c', fontSize: { xs: '0.9em', sm: '1em' } },
    '& .MuiInputLabel-root.Mui-focused': { color: '#13334c' },
    '& .MuiOutlinedInput-input': { color: '#000000', fontSize: { xs: '0.95em', sm: '1em' }, padding: { xs: '0.85em 0.5em', sm: '0.7em 0.5em' } }
  };

  const countrySelectStyles = {
    width: 'max-content',
    fieldset: { display: 'none' },
    "&.Mui-focused:has(div[aria-expanded='false'])": {
      fieldset: { display: 'block' }
    },
    '& .MuiSelect-icon': {
      right: '4px',
      color: 'black'
    },
    '.MuiSelect-select': {
      padding: '8px',
      paddingRight: '30px !important'
    },
    svg: { right: 0 }
  };

  const renderCountrySelect = (selectedCountry, onChangeHandler) => (
    <InputAdornment position='start' style={{ marginRight: '8px', marginLeft: '-10px' }}>
      <Select
        MenuProps={{
          style: {
            height: '300px',
            width: '220px',
            top: '10px',
            left: '-34px'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
        sx={countrySelectStyles}
        value={selectedCountry.iso2}
        onChange={(e) => onChangeHandler(e.target.value)}
        renderValue={(value) => (
          <FlagImage iso2={value} style={{ display: 'flex', width: '25px', height: 'auto' }} />
        )}
      >
        {defaultCountries.map((c) => {
          const country = parseCountry(c);
          return (
            <MenuItem
              key={country.iso2}
              value={country.iso2}
              sx={{
                color: 'black',
                backgroundColor: 'white',
                fontSize: '0.875rem',
                padding: '8px 16px',
                '& .MuiTypography-root': {
                  fontSize: '0.75rem'
                },
                '& .MuiTypography-colorGray': {
                  color: '#4292fe'
                }
              }}
            >
              <FlagImage
                iso2={country.iso2}
                style={{
                  marginRight: '8px',
                  width: '20px',
                  height: 'auto'
                }}
              />
              <Typography marginRight='8px' sx={{ fontSize: '0.75rem' }}>
                {country.name}
              </Typography>
              <Typography color='gray' sx={{ fontSize: '0.75rem' }}>
                +{country.dialCode}
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        backgroundColor: '#f9f9f9',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        margin: 'auto'
      }}
    >
      <Typography
        variant='h5'
        gutterBottom
        sx={{
          color: '#1976d2',
          textAlign: 'center',
          mb: 3,
          fontWeight: 'bold'
        }}
      >
        <Phone sx={{ verticalAlign: 'middle', mr: 1 }} />
        Contact Information
      </Typography>

      {/* Primary Phone Number */}
      <TextField
        variant='outlined'
        label='Phone Number'
        fullWidth
        required
        placeholder='Phone number'
        value={inputValue}
        onChange={handlePhoneValueChange}
        error={!!formik.errors.phone_number}
        helperText={formik.errors.phone_number}
        type='tel'
        inputRef={inputRef}
        InputProps={{
          startAdornment: renderCountrySelect(country, setCountry),
          endAdornment: (
            <InputAdornment position='end'>
              {loading
                ? (
                  <CircularProgress size={20} />
                  )
                : isVerified
                  ? (
                    <CheckCircle color='success' />
                    )
                  : (
                    <ErrorOutline color='error' />
                    )}
            </InputAdornment>
          )
        }}
        sx={textFieldStyles}
      />

      {/* Verify Phone Button */}
      <Button
        variant='contained'
        color='primary'
        onClick={handleSendOTP}
        disabled={isOTPRequested || formik.values.phone_number.length < 10}
        sx={{ mt: 2, ml: 2 }}
      >
        {isOTPRequested ? 'OTP Sent' : 'Verify Phone'}
      </Button>

      {/* OTP Input */}
      {isOTPRequested && (
        <Grid item xs={8}>
          <TextField
            name='otp'
            label='Enter OTP'
            fullWidth
            required
            error={!!otpError}
            helperText={otpError}
            onChange={(e) => setOTP(e.target.value)}
            value={otp}
            sx={{ ...textFieldStyles, width: '7em', marginRight: '20px' }}
          />
          <Button
            variant='contained'
            color='secondary'
            onClick={handleVerifyOTP}
            sx={{ mt: 2.2 }}
          >
            Verify OTP
          </Button>
        </Grid>
      )}

      {/* Additional Phone Number */}
      <TextField
        variant='outlined'
        label='Additional Phones'
        fullWidth
        placeholder='Additional Phones'
        value={inputValue}
        onChange={handlePhoneValueChange}
        error={!!formik.values.contact_info?.additional_phones}
        helperText={formik.errors.additional_phones}
        type='tel'
        inputRef={inputRef}
        InputProps={{
          startAdornment: renderCountrySelect(country, setCountry)
        }}
        sx={textFieldStyles}
      />

      {/* Emergency Contact */}
      <TextField
        variant='outlined'
        label='Emergency Contact'
        fullWidth
        placeholder='Emergency Contact'
        value={inputValue}
        onChange={handlePhoneValueChange}
        error={!!formik.values.contact_info?.emergency_contact}
        helperText={formik.errors.emergency_contact}
        type='tel'
        inputRef={inputRef}
        InputProps={{
          startAdornment: renderCountrySelect(country, setCountry)
        }}
        sx={textFieldStyles}
      />

      {/* Email */}
      <TextField
        name='email'
        label='Email'
        fullWidth
        required
        error={!!formik.errors.email}
        helperText={formik.errors.email}
        onChange={formik.handleChange}
        value={formik.values.email}
        sx={{ ...textFieldStyles, marginBottom: '10px' }}
      />

      {/* Website */}
      <TextField
        name='contact_info.website'
        label='Website'
        fullWidth
        onChange={formik.handleChange}
        value={formik.values.contact_info?.website || ''}
        sx={textFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Language sx={{ color: '#1976d2' }} />
            </InputAdornment>
          )
        }}
      />

      {/* Address Section */}
      <Typography variant='h6' sx={{ mt: 2 }}>Address</Typography>
      <Grid container spacing={2}>
        {[
          { name: 'street', label: 'Street' },
          { name: 'city', label: 'City' },
          { name: 'state', label: 'State' },
          { name: 'country', label: 'Country' },
          { name: 'postal_code', label: 'Postal Code' },
          { name: 'gps_coordinates', label: 'GPS Coordinates', required: false }
        ].map((field) => (
          <Grid item xs={4} sm={4} key={field.name}>
            <TextField
              label={field.label}
              name={`address.${field.name}`}
              value={formik.values.address[field.name]}
              onChange={formik.handleChange}
              fullWidth
              required={field.required !== false}
              sx={textFieldStyles}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ContactInformationStep;
